import { useEffect, useState } from 'react';
import { NavLink, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useOrg } from '@/tenancy/OrgContext';
import OrgSwitcher from '@/tenancy/OrgSwitcher';
import EmptyState from './EmptyState';
import ThemeToggle from '@/theme/ThemeToggle';
import EstimateModeSelect from '@/estimate/EstimateModeSelect';
import CreditsWidget from './CreditsWidget';
import SupportMenu from './SupportMenu';
import { auth as authApi } from '@/api/auth';
import { GITHUB_APP_INSTALL_URL } from '@/lib/config';
import { DemoBanner, DemoHint } from '@/demo/DemoUx';

const navItems = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/analyses', label: 'Analyses' },
  { to: '/projects', label: 'Projects' },
  { to: '/developers', label: 'Developers' },
  { to: '/billing', label: 'Billing' },
  { to: '/settings', label: 'Settings' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { hasOrgs } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const [resentVerification, setResentVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await authApi.resendVerification();
      setResentVerification(true);
    } finally {
      setResending(false);
    }
  };

  const initial = (user?.name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase();
  const showVerifyBanner = !!user && !user.emailVerifiedAt;

  return (
    <div className="app-shell">
      <div
        className={`sidebar-backdrop${sidebarOpen ? ' is-open' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />
      <aside className={`sidebar${sidebarOpen ? ' is-open' : ''}`}>
        <Link to="/dashboard" className="brand">
          <span className="brand-dot" />
          InsightPM
        </Link>

        {hasOrgs ? (
          <div className="sidebar-org">
            <OrgSwitcher />
          </div>
        ) : null}

        <nav className="nav">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-card">
            <div className="avatar avatar-fallback">{initial}</div>
            <div className="user-meta">
              <div className="user-name">{user?.name ?? user?.email}</div>
              <div className="muted small">{user?.email}</div>
            </div>
          </div>
          <SupportMenu />
          <button className="btn btn-ghost block" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="main">
        <DemoBanner />
        {showVerifyBanner && (
          <div className="verify-banner" role="status">
            {resentVerification ? (
              <span>Verification email sent — check your inbox.</span>
            ) : (
              <>
                <span>Please verify your email to invite teammates.</span>
                <button
                  className="btn btn-ghost"
                  onClick={handleResendVerification}
                  disabled={resending}
                >
                  {resending ? 'Sending…' : 'Resend verification email'}
                </button>
              </>
            )}
          </div>
        )}
        <div className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="mobile-nav-toggle"
              aria-label="Open navigation"
              onClick={() => setSidebarOpen(v => !v)}
            >
              ☰
            </button>
          </div>
          <div className="topbar-right">
            {hasOrgs ? <EstimateModeSelect /> : null}
            {hasOrgs ? <CreditsWidget /> : null}
            <ThemeToggle />
          </div>
        </div>

        <div className="content">
          {hasOrgs ? (
            <>
              <DemoHint />
              <Outlet />
            </>
          ) : (
            <EmptyState
              title="No organizations yet"
              description="Install the GitHub App on a repository to start analyzing pull requests."
              action={
                <a
                  className="btn btn-primary"
                  href={GITHUB_APP_INSTALL_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Install GitHub App
                </a>
              }
            />
          )}
        </div>
      </main>
    </div>
  );
}
